import { PERMISSIONS } from '@nks/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Flex, Input, Skeleton, Switch, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { useHasPermission } from '../auth/session';
import {
  CASE_CATEGORIES,
  CASE_SEVERITIES,
  REMINDER_RULE_CODES,
  getReminderRules,
  getSlaTargets,
  saveReminderRules,
  saveSlaTargets,
  type CaseCategory,
  type CaseSeverity,
  type ReminderRule,
  type ReminderRuleCode,
  type SlaTarget,
} from './api';
import { categoryLabel, severityLabel } from './labels';

const { Text, Title } = Typography;

/** Largest value the API accepts for minutes: one year. */
const MAX_MINUTES = 525_600;
const MAX_MONTHS = 120;
const MAX_KM = 200_000;

/** A whole number from 1 to max, or null. Blank, zero, decimals and text are all null. */
function wholeNumber(raw: string, max: number): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const n = Number(raw);
  return n >= 1 && n <= max ? n : null;
}

const cellKey = (c: CaseCategory, s: CaseSeverity): string => `${c}:${s}`;

interface SlaDraft {
  response: string;
  resolve: string;
}

function SlaCard({ initial }: { initial: SlaTarget[] }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, SlaDraft>>(() =>
    Object.fromEntries(
      initial.map((x) => [
        cellKey(x.category, x.severity),
        { response: String(x.responseMinutes), resolve: String(x.resolveMinutes) },
      ]),
    ),
  );
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const cells = CASE_CATEGORIES.flatMap((category) =>
    CASE_SEVERITIES.map((severity) => {
      const d = draft[cellKey(category, severity)] ?? { response: '', resolve: '' };
      const response = wholeNumber(d.response, MAX_MINUTES);
      const resolve = wholeNumber(d.resolve, MAX_MINUTES);
      const names = { category: categoryLabel(t, category), severity: severityLabel(t, severity) };
      const problem =
        response === null || resolve === null
          ? t('serviceDesk.settings.sla.badNumber', names)
          : response > resolve
            ? t('serviceDesk.settings.sla.order', names)
            : null;
      return { category, severity, d, response, resolve, problem, names };
    }),
  );
  const problems = cells.filter((c) => c.problem !== null);
  // A blank cell is being typed into; say nothing until something wrong is there.
  const shown = problems.filter((c) => c.d.response.length > 0 && c.d.resolve.length > 0);

  const edit = (c: CaseCategory, s: CaseSeverity, field: keyof SlaDraft, value: string): void => {
    setFailure(null);
    setDraft((prev) => ({
      ...prev,
      [cellKey(c, s)]: { response: '', resolve: '', ...prev[cellKey(c, s)], [field]: value },
    }));
  };

  const save = async (): Promise<void> => {
    const targets: SlaTarget[] = cells.map((c) => ({
      category: c.category,
      severity: c.severity,
      responseMinutes: c.response ?? 0,
      resolveMinutes: c.resolve ?? 0,
    }));
    setSaving(true);
    setFailure(null);
    try {
      await saveSlaTargets(targets);
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'settings', 'sla'] });
      void message.success(t('serviceDesk.settings.saved'));
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('serviceDesk.settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={t('serviceDesk.settings.sla.title')}>
      <Flex vertical gap={16}>
        <Text type="secondary">{t('serviceDesk.settings.sla.help')}</Text>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(80px, 1fr) 1fr 1fr', gap: 8, alignItems: 'center' }}>
          <Text strong>{t('serviceDesk.settings.sla.category')}</Text>
          <Text strong>{t('serviceDesk.settings.sla.response')}</Text>
          <Text strong>{t('serviceDesk.settings.sla.resolve')}</Text>
          {cells.map((c) => (
            <div key={cellKey(c.category, c.severity)} style={{ display: 'contents' }}>
              <Text>
                {c.names.category}, {c.names.severity}
              </Text>
              <Input
                inputMode="numeric"
                size="large"
                status={c.response === null || (c.resolve !== null && c.response > c.resolve) ? 'error' : undefined}
                aria-label={t('serviceDesk.settings.sla.responseLabel', c.names)}
                value={c.d.response}
                onChange={(e) => { edit(c.category, c.severity, 'response', e.target.value); }}
              />
              <Input
                inputMode="numeric"
                size="large"
                status={c.resolve === null ? 'error' : undefined}
                aria-label={t('serviceDesk.settings.sla.resolveLabel', c.names)}
                value={c.d.resolve}
                onChange={(e) => { edit(c.category, c.severity, 'resolve', e.target.value); }}
              />
            </div>
          ))}
        </div>
        {shown.length > 0 && (
          <Flex vertical gap={2}>
            {shown.map((c) => (
              <Text key={cellKey(c.category, c.severity)} type="danger">
                {c.problem}
              </Text>
            ))}
          </Flex>
        )}
        {failure !== null && <Alert type="error" showIcon message={failure} />}
        <Flex justify="flex-end">
          <Button type="primary" size="large" loading={saving} disabled={problems.length > 0} onClick={() => void save()}>
            {t('serviceDesk.settings.sla.submit')}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}

interface RuleDraft {
  months: string;
  kilometres: string;
  isActive: boolean;
}

function RulesCard({ initial }: { initial: ReminderRule[] }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<ReminderRuleCode, RuleDraft>>(() => {
    const byCode = (code: ReminderRuleCode): RuleDraft => {
      const r = initial.find((x) => x.code === code);
      return { months: String(r?.months ?? ''), kilometres: String(r?.kilometres ?? ''), isActive: r?.isActive ?? true };
    };
    return { FIRST_SERVICE: byCode('FIRST_SERVICE'), REGULAR: byCode('REGULAR') };
  });
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const rows = REMINDER_RULE_CODES.map((code) => {
    const d = draft[code];
    const months = wholeNumber(d.months, MAX_MONTHS);
    const kilometres = wholeNumber(d.kilometres, MAX_KM);
    const rule = t(`serviceDesk.reminderRule.${code}`);
    return { code, d, months, kilometres, rule, bad: months === null || kilometres === null };
  });
  const problems = rows.filter((r) => r.bad);
  const shown = problems.filter((r) => r.d.months.length > 0 && r.d.kilometres.length > 0);

  const edit = (code: ReminderRuleCode, patch: Partial<RuleDraft>): void => {
    setFailure(null);
    setDraft((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);
    try {
      await saveReminderRules(
        rows.map((r) => ({ code: r.code, months: r.months ?? 0, kilometres: r.kilometres ?? 0, isActive: r.d.isActive })),
      );
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'settings', 'rules'] });
      void message.success(t('serviceDesk.settings.saved'));
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('serviceDesk.settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={t('serviceDesk.settings.rules.title')}>
      <Flex vertical gap={16}>
        <Text type="secondary">{t('serviceDesk.settings.rules.help')}</Text>
        {rows.map((r) => (
          <Flex key={r.code} vertical gap={8}>
            <Text strong>{r.rule}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.code === 'FIRST_SERVICE'
                ? t('serviceDesk.settings.rules.afterDelivery')
                : t('serviceDesk.settings.rules.afterService')}
            </Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center' }}>
              <Input
                inputMode="numeric"
                size="large"
                suffix={t('serviceDesk.settings.rules.months')}
                status={r.months === null ? 'error' : undefined}
                aria-label={t('serviceDesk.settings.rules.monthsLabel', { rule: r.rule })}
                value={r.d.months}
                onChange={(e) => { edit(r.code, { months: e.target.value }); }}
              />
              <Input
                inputMode="numeric"
                size="large"
                suffix="km"
                status={r.kilometres === null ? 'error' : undefined}
                aria-label={t('serviceDesk.settings.rules.kmLabel', { rule: r.rule })}
                value={r.d.kilometres}
                onChange={(e) => { edit(r.code, { kilometres: e.target.value }); }}
              />
              <Switch
                checked={r.d.isActive}
                aria-label={t('serviceDesk.settings.rules.activeLabel', { rule: r.rule })}
                onChange={(checked) => { edit(r.code, { isActive: checked }); }}
              />
            </div>
          </Flex>
        ))}
        {shown.map((r) => (
          <Text key={r.code} type="danger">
            {t('serviceDesk.settings.rules.badNumber', { rule: r.rule })}
          </Text>
        ))}
        {failure !== null && <Alert type="error" showIcon message={failure} />}
        <Flex justify="flex-end">
          <Button type="primary" size="large" loading={saving} disabled={problems.length > 0} onClick={() => void save()}>
            {t('serviceDesk.settings.rules.submit')}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}

/** Case clocks and service-reminder timing. Company-wide, for people who may configure the service desk. */
export function ServiceDeskSettingsPage(): ReactNode {
  const { t } = useTranslation();
  const canConfigure = useHasPermission(PERMISSIONS.SERVICE_DESK_CONFIGURE);
  const sla = useQuery({ queryKey: ['service-desk', 'settings', 'sla'], queryFn: getSlaTargets, enabled: canConfigure });
  const rules = useQuery({ queryKey: ['service-desk', 'settings', 'rules'], queryFn: getReminderRules, enabled: canConfigure });

  if (!canConfigure) {
    return (
      <Card>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('serviceDesk.settings.forbidden')} />
      </Card>
    );
  }

  const failed = sla.error ?? rules.error;
  return (
    <Flex vertical gap={16}>
      <Title level={4} style={{ margin: 0 }}>
        {t('serviceDesk.settings.title')}
      </Title>
      {failed !== null && (
        <Alert
          type="error"
          showIcon
          message={failed instanceof ApiError ? failed.message : t('serviceDesk.settings.loadFailed')}
        />
      )}
      {sla.data !== undefined ? <SlaCard initial={sla.data.targets} /> : failed === null && <Skeleton active />}
      {rules.data !== undefined ? <RulesCard initial={rules.data.rules} /> : failed === null && <Skeleton active />}
    </Flex>
  );
}
